import { styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import auth from 'services/auth';
import courseRequests from 'services/course';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    Divider,
    Icon,
    IconButton,
    MultiInput,
    ResponsiveDialog,
    Stack,
    Tabs,
    Text,
    TextInput,
    Tooltip,
} from 'shell/components';

const CloningContents = ({ open, setOpen, course, generatedIdList, setGeneratedIdList }) => {
    const { message } = useSnackbar();
    const { device } = useViewport();

    const inputRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);
    const [findContentLoading, setFindContentLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [copyingContents, setCopyingContents] = useState({ milestones: 0, taskTemplates: 0 });
    const [errors, setErrors] = useState({
        COPY_TO_MULTI_INPUT: '',
        COPY_FROM_TEXT_INPUT: '',
    });
    const [dialog, setDialog] = useState({ open: false });

    const handleClose = () => {
        if (!isLoading) {
            setOpen((prev) => ({ ...prev, open: false }));
            setGeneratedIdList([]);
            setInputValue('');
            setCopyingContents({ milestones: 0, taskTemplates: 0 });
            setIsLoading(false);
            setFindContentLoading(false);
            setTab('copy-to');
            setErrors({
                COPY_TO_MULTI_INPUT: '',
                COPY_FROM_TEXT_INPUT: '',
            });
            setDialog({ open: false });
        }
    };

    const handleCourseIdValidation = async (generatedId) => {
        if (generatedId === course.generated_id) return message.error('Please do not enter current course ID.');
        const courseContent = await courseRequests.findCourseCloningContents(generatedId);
        return courseContent ? false : true;
    };

    const handleFindContent = async () => {
        if (!inputValue) {
            setErrors((prev) => ({ ...prev, COPY_FROM_TEXT_INPUT: 'Please enter course ID' }));
            return message.error('Please enter course ID.');
        }
        if (inputValue === course.generated_id) return message.error('Please do not enter current course ID.');
        setFindContentLoading(true);
        const validation = await handleSettingText();
        if (!validation) {
            setFindContentLoading(false);
            return message.error('The course does not exist.');
        }
        setGeneratedIdList([{ key: inputValue, value: inputValue, error: false }]);

        setFindContentLoading(false);
    };

    const openDialog = async () => {
        if (generatedIdList?.length === 0) {
            return setErrors((prev) => ({ ...prev, COPY_TO_MULTI_INPUT: 'Please enter Course ID(s)' }));
        }
        if (!generatedIdList.every(({ error }) => !error)) {
            setIsLoading(false);
            setErrors((prev) => ({ ...prev, COPY_TO_MULTI_INPUT: 'Course ID is not valid' }));
            return message.error('Please check your ids.');
        }
        if (tab === 'copy-from') {
            const courseExist = await courseRequests.findCourseCloningContents(inputValue);
            if (!courseExist) {
                setIsLoading(false);
                return message.error('The id does not exist.');
            }
        }
        if (!course.generated_id) {
            setIsLoading(false);
            return message.error('Please generated the course id.');
        }
        setDialog({ open: true });
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        const data = {
            type: tab,
            generated_id_copy_from: tab === 'copy-to' ? course.generated_id : generatedIdList[0].value,
            generated_id_list: tab === 'copy-to' ? generatedIdList.map((id) => id.value) : [course.generated_id],
        };
        await courseRequests.createCourseCloneContents(auth.course_id, data);
        message.success(`Content was successfully copied to (${generatedIdList.length}) course.`);
        setIsLoading(false);
        handleClose();
    };

    const handleRemoveChip = (_, targetIndex) => {
        setGeneratedIdList((current) => current.filter((_, index) => index !== targetIndex));
    };

    const handleAddChip = async (value) => {
        if (!value?.trim()) return;
        setInputValue('');
        const duplicatedValue = generatedIdList.find((idObj) => idObj.value === value);
        if (duplicatedValue) {
            return setErrors((prev) => ({ ...prev, COPY_TO_MULTI_INPUT: 'The course ID already exists' }));
        }
        setErrors((prev) => ({ ...prev, COPY_TO_MULTI_INPUT: '' }));
        setGeneratedIdList((current) => [...current, { key: value.trim(), value: value.trim(), loading: true }]);
        const error = await handleCourseIdValidation(value);
        setGeneratedIdList((current) => {
            return current.map((item) => {
                return item.key === value.trim() ? { ...item, loading: false, error } : item;
            });
        });
    };

    const handleSettingText = async () => {
        if (!course) return;
        let courseId = course.course_id;
        if (tab === 'copy-from') {
            const copyingCourse = await courseRequests.findCourseCloningContents(inputValue);
            if (copyingCourse) {
                courseId = copyingCourse.id;
            } else {
                return false;
            }
        }
        const taskTemplates = await courseRequests.listTaskTemplates(courseId);
        const milestones = await courseRequests.listMilestones(courseId);
        setCopyingContents({ milestones: milestones.length, taskTemplates: taskTemplates.length });
        return true;
    };

    const canSubmit = !isLoading && generatedIdList.every(({ error }) => !error);

    const CopyTo = (
        <Stack sx={{ mt: 5 }} spacing={7}>
            <Stack spacing={2}>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Text variant={'p2'}>Copy To (Course ID(s))</Text>
                    <Tooltip
                        content={
                            <Stack divider={<Divider />} spacing={1}>
                                <Text color={'inherit'} variant={'p3'}>
                                    Where to Find Course ID?
                                </Text>
                                <Text color={'inherit'} variant={'p3'}>
                                    From the course dashboard, select the course you would like to copy content to and
                                    navigate to the course overview tab. If you are not a member of the course you would
                                    like to copy content to, please request this ID from a current instructor or
                                    administrator.
                                </Text>
                            </Stack>
                        }
                        type={'info'}
                    />
                </Stack>
                <MultiInput
                    error={Boolean(errors.COPY_TO_MULTI_INPUT)}
                    helperText={errors.COPY_TO_MULTI_INPUT}
                    inputValue={inputValue}
                    placeholder={'Enter Course ID(s), comma separated'}
                    value={generatedIdList}
                    onChange={(e) => setInputValue(e.target.value)}
                    onAddChip={handleAddChip}
                    onRemoveChip={handleRemoveChip}
                />
            </Stack>
            <Stack alignItems={'flex-start'} sx={{ minHeight: '200px' }}>
                <Stack alignItems={'center'} direction={'row'} spacing={3}>
                    <Checkbox defaultChecked disabled />
                    <Stack>
                        <Text variant={'p2'}>Milestones & Task Templates</Text>
                        <Text color={'light'} variant={'p3'}>
                            {copyingContents.milestones} Milestones, and {copyingContents.taskTemplates} Task Templates
                            will be copied.
                        </Text>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );

    const CopyFrom = (
        <Stack sx={{ mt: 5 }} spacing={7}>
            <Stack spacing={2}>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Text variant={'p2'}>Copy From (Course ID)</Text>
                    <Tooltip
                        content={
                            <Stack divider={<Divider />} spacing={1}>
                                <Text color={'inherit'} variant={'p3'}>
                                    Where to Find Course ID?
                                </Text>
                                <Text color={'inherit'} variant={'p3'}>
                                    From the course dashboard, select the course you would like to copy content to and
                                    navigate to the course overview tab. If you are not a member of the course you would
                                    like to copy content to, please request this ID from a current instructor or
                                    administrator.
                                </Text>
                            </Stack>
                        }
                        type={'info'}
                    />
                </Stack>
                <Stack alignItems={'flex-start'} direction={'row'} spacing={2}>
                    <TextInput
                        ref={inputRef}
                        error={errors.COPY_FROM_TEXT_INPUT ? true : false}
                        helperText={errors.COPY_FROM_TEXT_INPUT}
                        placeholder={'Enter a Course ID'}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        sx={{ width: '80%' }}
                    />
                    <Button
                        disabled={findContentLoading}
                        loading={findContentLoading}
                        variant={'primary'}
                        onClick={() => handleFindContent()}
                    >
                        Find Content
                    </Button>
                </Stack>
            </Stack>
            {generatedIdList?.length > 0 && generatedIdList?.some(({ error }) => !error) ? (
                <Stack alignItems={'flex-start'} sx={{ minHeight: '200px' }}>
                    <Stack alignItems={'center'} direction={'row'} spacing={3}>
                        <Checkbox defaultChecked disabled />
                        <Stack>
                            <Text variant={'p2'}>Milestones & Task Templates</Text>
                            <Text color={'light'} variant={'p3'}>
                                {copyingContents.milestones} Milestones, and {copyingContents.taskTemplates} Task
                                Templates will be copied.
                            </Text>
                        </Stack>
                    </Stack>
                </Stack>
            ) : (
                <EmptyBox>
                    <Stack alignItems={'center'} spacing={5}>
                        <Text color={'light'} variant={'p2'}>
                            Nothing to show yet! Please enter a Course ID and click “Find Content.”
                        </Text>
                    </Stack>
                </EmptyBox>
            )}
        </Stack>
    );

    const options = [
        { key: 'copy-to', value: 'Copy To', content: CopyTo },
        { key: 'copy-from', value: 'Copy From', content: CopyFrom },
    ];

    const [tab, setTab] = useState(options[0].key);

    useEffect(() => {
        setGeneratedIdList([]);
        setInputValue('');
        setCopyingContents({ milestones: 0, taskTemplates: 0 });
    }, [tab]);

    useEffect(() => {
        if (course) {
            if (tab === 'copy-to') {
                (async () => {
                    await handleSettingText();
                })();
            }
        }
    }, [course]);

    return (
        <ResponsiveDialog open={open} onClose={handleClose}>
            <RootStack device={device} spacing={4}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'h6'}>Copy Project Content</Text>
                    <IconButton
                        disabled={isLoading}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
                <Stack spacing={6}>
                    <Tabs
                        aria-label={'select cloning content method'}
                        options={options}
                        selected={tab}
                        onChange={setTab}
                    />
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-end'}>
                        <Stack alignItems={'center'} direction={'row'} spacing={5}>
                            <Button color={'neutral'} variant={'tertiary'} onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button disabled={!canSubmit} loading={isLoading} variant={'primary'} onClick={openDialog}>
                                {tab === 'copy-to' ? 'Export' : 'Copy'}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </RootStack>
            <Dialog open={dialog.open} onClose={() => !isLoading && setDialog({ open: false })}>
                <Stack spacing={7} sx={{ width: '232px' }}>
                    <Stack alignItems={'center'} justifyContent={'center'}>
                        <CustomIcon kind={'error'} />
                        <Text color={'primary'}>Confirm Copy</Text>
                    </Stack>
                    <Stack>
                        <Text>
                            This will copy the following items to ({generatedIdList.length}) selected course(s):
                        </Text>
                        <Text component={'li'}>
                            Milestones
                            <Text component={'span'} color={'primary'}>
                                {` (${copyingContents.milestones})`}
                            </Text>
                        </Text>
                        <Text component={'li'}>
                            Task Templates
                            <Text component={'span'} color={'primary'}>
                                {` (${copyingContents.taskTemplates})`}
                            </Text>
                        </Text>
                    </Stack>

                    <Stack alignItems={'center'} direction={'row'} justifyContent={'center'}>
                        <Stack alignItems={'center'} direction={'row'} spacing={8}>
                            <Button variant={'tertiary'} onClick={() => setDialog({ open: false })}>
                                Cancel
                            </Button>
                            <Button loading={isLoading} variant={'secondary'} onClick={() => handleSubmit()}>
                                Confirm
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Dialog>
        </ResponsiveDialog>
    );
};

const RootStack = styled(Stack)(({ device }) => {
    return {
        width: device === 'mobile' ? '100%' : 620,
    };
});

const EmptyBox = styled(Box)(({ theme }) => {
    return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: theme.spacing(2),
        background: theme.palette.ensightful.neutral[100],
        width: '100%',
        height: '200px',
    };
});

const CustomIcon = styled(Icon)(({ theme }) => {
    return {
        borderRadius: '50%',
        marginBottom: theme.spacing(2),
        padding: '0.3125rem',
        fontSize: '1.5rem',
        background: theme.palette.ensightful.primary[500],
        color: theme.palette.ensightful.shades[0],
    };
});

export default CloningContents;
